import * as Sentry from "@sentry/react";
import {
	APICallError,
	type ModelMessage,
	RetryError,
	type ToolApprovalResponse,
	streamText,
} from "ai";
import { nanoid } from "nanoid";
import { getAgentChatSession } from "./agent-chat-session-store";
import { agentChatMessagesToModelMessages } from "./agent-messages-to-model";
import { trackAgentBoardToolMutation } from "./agent-session-board-mutations";
import {
	clearAgentSessionTouchedShapes,
	getAgentSessionTouchedShapes,
} from "./agent-session-shape-tracker";
import {
	type AgentSkillAvailability,
	withLoadedSkillInstructions,
} from "./agent-skills";
import {
	type AgentChatStreamPart,
	createAgentChatStreamParserState,
	processAgentChatStreamPart,
} from "./agent-stream-parser";
import {
	AGENT_CHAT_TELEMETRY,
	CHAT_ONLY_TOOL_NAMES,
	isAgentBoardMutationTool,
	stopWhenPresentOptionsOrMaxSteps,
} from "./board-agent-config";
import { ensureAssistantVisibleReply } from "./ensure-assistant-visible-reply";
import { createIllumiProvider } from "./illumi-llm-provider";
import { getPendingChoice } from "./present-options";
import {
	appendToolApprovalMessage,
	getPendingApprovals,
} from "./tool-approvals";
import { requiresWhiteboardLifecycleApproval } from "./tool-registry";
import type {
	AgentChatAssistantMessage,
	AgentChatAssistantPart,
	AgentChatAttachment,
	AgentChatMessage,
} from "./types";

export { createIllumiProvider } from "./illumi-llm-provider";

export { DEFAULT_LLM_MODEL } from "@src/config/constants";

export function createUserMessage(
	content: string,
	attachments?: AgentChatAttachment[],
): AgentChatMessage {
	return {
		id: nanoid(),
		role: "user",
		content,
		createdAt: Date.now(),
		...(attachments && attachments.length > 0 ? { attachments } : {}),
	};
}

export function createAssistantMessage(
	id: string,
	parts: AgentChatAssistantPart[],
	createdAt = Date.now(),
): AgentChatAssistantMessage {
	return {
		id,
		role: "assistant",
		parts,
		createdAt,
	};
}

export function mapAgentChatHttpError(status: number): string {
	if (status === 401) {
		return "Session expired - please sign in again";
	}
	if (status === 403) {
		return "LLM gateway key not configured for this user";
	}
	if (status === 404) {
		return "LLM endpoint not found - check base URL and path";
	}
	if (status === 429) {
		return "Too many requests or out of credits";
	}
	if (status >= 500) {
		return "LLM service is temporarily unavailable";
	}
	return `LLM request failed (${status})`;
}

/**
 * Parts that already finished and should survive a mid-stream failure
 * (e.g. generateImageInChat succeeded, then the follow-up chat/completions
 * step hit 429). Streaming text/reasoning is dropped.
 */
export function collectDurableAssistantParts(
	parts: AgentChatAssistantPart[],
): AgentChatAssistantPart[] {
	return parts.filter((part) => {
		switch (part.type) {
			case "tool-call":
				return part.state === "completed" || part.state === "awaiting_approval";
			case "tool-result":
			case "generated-image":
			case "choice-request":
			case "choice-response":
			case "approval-request":
			case "approval-response":
			case "action-receipt":
				return true;
			case "text":
			case "reasoning":
				return false;
			default:
				return false;
		}
	});
}

/** Prefer gateway body detail when it explains a 429 better than the generic map. */
export function formatAgentChatCaughtError(
	caught: unknown,
	statusCode: number | null,
): string {
	const detailed = extractErrorMessage(caught);
	const nestedDetail =
		RetryError.isInstance(caught) && caught.lastError
			? extractErrorMessage(caught.lastError)
			: "";
	const rateLimitDetail =
		extractRateLimitDetail(detailed) ?? extractRateLimitDetail(nestedDetail);

	if (statusCode === 429) {
		if (rateLimitDetail) {
			return rateLimitDetail;
		}
		return mapAgentChatHttpError(429);
	}

	if (statusCode !== null) {
		return mapAgentChatHttpError(statusCode);
	}

	return detailed;
}

function extractRateLimitDetail(message: string): string | null {
	const start = message.search(/Rate limit exceeded/i);
	if (start < 0) {
		return null;
	}

	const line = message.slice(start).split("\n")[0] ?? "";
	const cleaned = line
		.replace(/\s*for api_key:\s*[a-f0-9]+/gi, "")
		.replace(/\s+/g, " ")
		.trim();

	return cleaned.length > 0 ? cleaned : null;
}

const whiteboardLifecycleToolApproval = ({
	toolCall,
}: {
	toolCall: { toolName: string };
}) =>
	requiresWhiteboardLifecycleApproval(toolCall.toolName)
		? ("user-approval" as const)
		: ("not-applicable" as const);

type SharedStreamParams = {
	llmModel: string;
	agentTools: Parameters<typeof streamText>[0]["tools"];
	system: string;
	abortSignal: AbortSignal;
	onMessagesChange: (messages: AgentChatMessage[]) => void;
	onToolExecutionEnd?: (toolName: string) => void;
	requestAnimationFrame?: (cb: () => void) => number;
	cancelAnimationFrame?: (id: number) => void;
	/**
	 * After the user denies every pending approval, continue with no active
	 * tools so the model can only acknowledge — not create cards / shapes as a
	 * workaround.
	 */
	textOnlyAfterDenial?: boolean;
	skillAvailability?: AgentSkillAvailability;
};
export type RunAgentChatTurnParams = SharedStreamParams & {
	text: string;
	attachments?: AgentChatAttachment[];
	baseMessages: AgentChatMessage[];
};

export type ContinueAgentChatAfterApprovalsParams = SharedStreamParams & {
	messages: AgentChatMessage[];
	continuationMessages: ModelMessage[];
	approvals: ToolApprovalResponse[];
};

export type RunAgentChatTurnResult =
	| {
			ok: true;
			messages: AgentChatMessage[];
			awaitingChoice: boolean;
			awaitingApproval: boolean;
			continuationMessages?: ModelMessage[];
			touchedBoard: boolean;
			touchedShapeIds: string[];
			aborted: boolean;
	  }
	| {
			ok: false;
			error: string;
			aborted: boolean;
	  };

async function streamAgentChatMessages({
	modelMessages,
	threadMessages,
	assistantMessageId,
	assistantCreatedAt,
	seedParts = [],
	agentTools,
	system,
	abortSignal,
	onMessagesChange,
	onToolExecutionEnd,
	requestAnimationFrame: raf = (cb) => window.requestAnimationFrame(cb),
	cancelAnimationFrame: caf = (id) => window.cancelAnimationFrame(id),
	textOnlyAfterDenial = false,
	llmModel,
}: SharedStreamParams & {
	modelMessages: ModelMessage[];
	threadMessages: AgentChatMessage[];
	assistantMessageId: string;
	assistantCreatedAt: number;
	seedParts?: AgentChatAssistantPart[];
}): Promise<RunAgentChatTurnResult> {
	const provider = await createIllumiProvider();
	const parserState = createAgentChatStreamParserState();
	parserState.assistantParts = [...seedParts];
	let hasAssistantMessage = seedParts.length > 0;
	let scheduledSyncFrame: number | null = null;
	const pendingToolInputs = new Map<string, unknown>();
	let touchedBoardThisTurn = false;

	// Keep tools registered for denial handling, but allow no new tool calls.
	const activeTools =
		textOnlyAfterDenial && agentTools
			? ([] as Array<keyof typeof agentTools & string>)
			: undefined;

	const flushAssistantMessage = () => {
		scheduledSyncFrame = null;
		const assistant = createAssistantMessage(
			assistantMessageId,
			[...parserState.assistantParts],
			assistantCreatedAt,
		);
		hasAssistantMessage = true;
		onMessagesChange([...threadMessages, assistant]);
	};

	const syncAssistantMessage = () => {
		if (scheduledSyncFrame !== null) {
			return;
		}

		scheduledSyncFrame = raf(flushAssistantMessage);
	};

	const flushPendingAssistantMessage = () => {
		if (scheduledSyncFrame === null) {
			return;
		}

		caf(scheduledSyncFrame);
		flushAssistantMessage();
	};

	if (hasAssistantMessage) {
		flushAssistantMessage();
	}

	const result = streamText({
		model: provider.chatModel(llmModel),
		system,
		messages: modelMessages,
		tools: agentTools,
		...(activeTools ? { activeTools } : {}),
		stopWhen: agentTools ? stopWhenPresentOptionsOrMaxSteps() : undefined,
		experimental_telemetry: AGENT_CHAT_TELEMETRY,
		abortSignal,
		toolApproval: whiteboardLifecycleToolApproval,
		onToolExecutionStart: ({ toolCall }) => {
			Sentry.addBreadcrumb({
				category: "agent-chat.tool",
				message: toolCall.toolName,
				level: "info",
			});
		},
		onToolExecutionEnd: ({ toolCall }) => {
			if (CHAT_ONLY_TOOL_NAMES.has(toolCall.toolName)) {
				return;
			}

			onToolExecutionEnd?.(toolCall.toolName);
		},
	});

	const resetAssistantOnFailure = () => {
		if (scheduledSyncFrame !== null) {
			caf(scheduledSyncFrame);
			scheduledSyncFrame = null;
		}

		const durableParts = collectDurableAssistantParts(
			parserState.assistantParts,
		);
		const partsToKeep =
			durableParts.length > 0
				? durableParts
				: collectDurableAssistantParts(seedParts);

		if (partsToKeep.length > 0) {
			onMessagesChange([
				...threadMessages,
				createAssistantMessage(
					assistantMessageId,
					partsToKeep,
					assistantCreatedAt,
				),
			]);
			return;
		}

		if (hasAssistantMessage) {
			onMessagesChange(threadMessages);
		}
	};

	try {
		for await (const streamPart of result.fullStream as AsyncIterable<AgentChatStreamPart>) {
			if (abortSignal.aborted) {
				if (scheduledSyncFrame !== null) {
					caf(scheduledSyncFrame);
					scheduledSyncFrame = null;
				}
				return {
					ok: true,
					messages:
						seedParts.length > 0
							? [
									...threadMessages,
									createAssistantMessage(
										assistantMessageId,
										seedParts,
										assistantCreatedAt,
									),
								]
							: threadMessages,
					awaitingChoice: false,
					awaitingApproval: false,
					touchedBoard: touchedBoardThisTurn,
					touchedShapeIds: getAgentSessionTouchedShapes(),
					aborted: true,
				};
			}

			if (streamPart.type === "error") {
				resetAssistantOnFailure();
				const err = streamPart.error;
				if (err instanceof Error) {
					throw err;
				}
				if (typeof err === "string") {
					throw new Error(err);
				}
				throw err ?? new Error("Stream encountered an error");
			}

			if (streamPart.type === "tool-call") {
				pendingToolInputs.set(streamPart.toolCallId, streamPart.input);
			}

			if (streamPart.type === "tool-result") {
				const input = streamPart.toolCallId
					? pendingToolInputs.get(streamPart.toolCallId)
					: undefined;
				trackAgentBoardToolMutation({
					toolName: streamPart.toolName,
					input,
					output: streamPart.output,
				});
				if (isAgentBoardMutationTool(streamPart.toolName)) {
					touchedBoardThisTurn = true;
				}

				if (streamPart.toolCallId) {
					pendingToolInputs.delete(streamPart.toolCallId);
				}
			}

			if (processAgentChatStreamPart(parserState, streamPart)) {
				syncAssistantMessage();
			}
		}
	} catch (err) {
		resetAssistantOnFailure();
		throw err;
	}

	flushPendingAssistantMessage();

	if (!hasAssistantMessage) {
		throw new Error("LLM returned an empty response");
	}

	const responseMessages =
		"responseMessages" in result && result.responseMessages != null
			? await result.responseMessages
			: [];
	const baseModelMessages = [...modelMessages, ...responseMessages];
	const awaitingApproval =
		getPendingApprovals([
			{
				id: assistantMessageId,
				role: "assistant",
				parts: parserState.assistantParts,
				createdAt: assistantCreatedAt,
			},
		]).length > 0;

	const assistantParts = awaitingApproval
		? [...parserState.assistantParts]
		: ensureAssistantVisibleReply([...parserState.assistantParts]);

	const completedThread = [
		...threadMessages,
		createAssistantMessage(
			assistantMessageId,
			assistantParts,
			assistantCreatedAt,
		),
	];

	return {
		ok: true,
		messages: completedThread,
		awaitingChoice:
			!awaitingApproval && Boolean(getPendingChoice(completedThread)),
		awaitingApproval,
		continuationMessages: awaitingApproval ? baseModelMessages : undefined,
		touchedBoard: touchedBoardThisTurn,
		touchedShapeIds: getAgentSessionTouchedShapes(),
		aborted: false,
	};
}

function extractHttpStatusCode(caught: unknown, depth = 0): number | null {
	if (depth > 5 || caught === null || caught === undefined) {
		return null;
	}

	if (
		APICallError.isInstance(caught) &&
		typeof caught.statusCode === "number"
	) {
		return caught.statusCode;
	}

	if (RetryError.isInstance(caught)) {
		const lastStatus = extractHttpStatusCode(caught.lastError, depth + 1);
		if (lastStatus !== null) {
			return lastStatus;
		}
		for (const err of caught.errors) {
			const status = extractHttpStatusCode(err, depth + 1);
			if (status !== null) {
				return status;
			}
		}
	}

	if (typeof caught === "object") {
		const candidate = caught as Record<string, unknown>;
		if (typeof candidate.statusCode === "number") {
			return candidate.statusCode;
		}
		if (typeof candidate.status === "number") {
			return candidate.status;
		}
		if (typeof candidate.response === "object" && candidate.response !== null) {
			const resp = candidate.response as Record<string, unknown>;
			if (typeof resp.status === "number") {
				return resp.status;
			}
			if (typeof resp.statusCode === "number") {
				return resp.statusCode;
			}
		}
		if (
			typeof candidate.lastError === "object" &&
			candidate.lastError !== null
		) {
			const status = extractHttpStatusCode(candidate.lastError, depth + 1);
			if (status !== null) {
				return status;
			}
		}
		if (typeof candidate.cause === "object" && candidate.cause !== null) {
			const status = extractHttpStatusCode(candidate.cause, depth + 1);
			if (status !== null) {
				return status;
			}
		}
		if (typeof candidate.error === "object" && candidate.error !== null) {
			const errorObj = candidate.error as Record<string, unknown>;
			if (typeof errorObj.code === "number") {
				return errorObj.code;
			}
			if (typeof errorObj.status === "number") {
				return errorObj.status;
			}
			const status = extractHttpStatusCode(candidate.error, depth + 1);
			if (status !== null) {
				return status;
			}
		}
		if (Array.isArray(candidate.errors)) {
			for (const err of candidate.errors) {
				const status = extractHttpStatusCode(err, depth + 1);
				if (status !== null) {
					return status;
				}
			}
		}
	}

	if (
		caught instanceof Error ||
		(typeof caught === "object" &&
			"message" in (caught as Record<string, unknown>))
	) {
		const message = String((caught as Record<string, unknown>).message ?? "");
		const match = message.match(/\b(401|403|404|429|5\d{2})\b/);
		if (match?.[1]) {
			return Number.parseInt(match[1], 10);
		}
		if (
			/\b(rate limit|out of credits|insufficient_quota|quota exceeded)\b/i.test(
				message,
			)
		) {
			return 429;
		}
	}

	return null;
}

function isApiCallError(caught: unknown, depth = 0): boolean {
	if (depth > 5 || caught === null || caught === undefined) {
		return false;
	}
	if (APICallError.isInstance(caught)) {
		return true;
	}
	if (RetryError.isInstance(caught)) {
		return (
			isApiCallError(caught.lastError, depth + 1) ||
			caught.errors.some((err) => isApiCallError(err, depth + 1))
		);
	}
	if (typeof caught === "object") {
		const candidate = caught as Record<string, unknown>;
		if (isApiCallError(candidate.cause, depth + 1)) {
			return true;
		}
		if (
			Array.isArray(candidate.errors) &&
			candidate.errors.some((err) => isApiCallError(err, depth + 1))
		) {
			return true;
		}
	}
	return false;
}

function extractErrorMessage(caught: unknown): string {
	if (caught instanceof Error && caught.message) {
		return caught.message;
	}
	if (typeof caught === "string" && caught.trim().length > 0) {
		return caught;
	}
	if (typeof caught === "object" && caught !== null) {
		const candidate = caught as Record<string, unknown>;
		if (
			typeof candidate.message === "string" &&
			candidate.message.trim().length > 0
		) {
			return candidate.message;
		}
		if (
			typeof candidate.error === "string" &&
			candidate.error.trim().length > 0
		) {
			return candidate.error;
		}
		if (
			typeof candidate.error === "object" &&
			candidate.error !== null &&
			typeof (candidate.error as Record<string, unknown>).message === "string"
		) {
			return (candidate.error as Record<string, unknown>).message as string;
		}
		if (candidate.cause) {
			const causeMsg = extractErrorMessage(candidate.cause);
			if (causeMsg !== "Something went wrong. Please try again.") {
				return causeMsg;
			}
		}
	}
	return "Something went wrong. Please try again.";
}

function mapCaughtError(caught: unknown): RunAgentChatTurnResult {
	if (
		(caught instanceof DOMException && caught.name === "AbortError") ||
		(caught instanceof Error && caught.name === "AbortError")
	) {
		return {
			ok: false,
			error: "aborted",
			aborted: true,
		};
	}

	const statusCode = extractHttpStatusCode(caught);
	const message = formatAgentChatCaughtError(caught, statusCode);

	const isExpectedApiError = statusCode !== null || isApiCallError(caught);

	if (!isExpectedApiError) {
		Sentry.captureException(caught);
	}

	return {
		ok: false,
		error: message,
		aborted: false,
	};
}

export async function runAgentChatTurn({
	text,
	attachments,
	baseMessages,
	llmModel,
	agentTools,
	system,
	abortSignal,
	onMessagesChange,
	onToolExecutionEnd,
	requestAnimationFrame: raf = (cb) => window.requestAnimationFrame(cb),
	cancelAnimationFrame: caf = (id) => window.cancelAnimationFrame(id),
	skillAvailability,
}: RunAgentChatTurnParams): Promise<RunAgentChatTurnResult> {
	const trimmed = text.trim();
	const userMessage = createUserMessage(trimmed, attachments);
	const nextMessages = [...baseMessages, userMessage];
	onMessagesChange(nextMessages);

	clearAgentSessionTouchedShapes();

	const modelMessages = agentChatMessagesToModelMessages(nextMessages);

	try {
		return await streamAgentChatMessages({
			modelMessages,
			threadMessages: nextMessages,
			assistantMessageId: nanoid(),
			assistantCreatedAt: Date.now(),
			llmModel,
			agentTools,
			system: withLoadedSkillInstructions(
				system,
				modelMessages,
				getAgentChatSession().loadedSkillNames,
				skillAvailability,
			),
			abortSignal,
			onMessagesChange,
			onToolExecutionEnd,
			requestAnimationFrame: raf,
			cancelAnimationFrame: caf,
		});
	} catch (caught) {
		return mapCaughtError(caught);
	}
}

export async function continueAgentChatAfterApprovals({
	messages,
	continuationMessages,
	approvals,
	llmModel,
	agentTools,
	system,
	abortSignal,
	onMessagesChange,
	onToolExecutionEnd,
	requestAnimationFrame: raf = (cb) => window.requestAnimationFrame(cb),
	cancelAnimationFrame: caf = (id) => window.cancelAnimationFrame(id),
	skillAvailability,
}: ContinueAgentChatAfterApprovalsParams): Promise<RunAgentChatTurnResult> {
	const lastAssistant = [...messages]
		.reverse()
		.find((message) => message.role === "assistant");
	if (!lastAssistant || lastAssistant.role !== "assistant") {
		return {
			ok: false,
			error: "No pending tool approval to continue",
			aborted: false,
		};
	}

	const threadMessages = messages.slice(0, -1);
	const modelMessages = appendToolApprovalMessage(
		continuationMessages,
		approvals,
	);
	const textOnlyAfterDenial = approvals.every(
		(approval) => approval.approved === false,
	);

	try {
		return await streamAgentChatMessages({
			modelMessages,
			threadMessages,
			assistantMessageId: lastAssistant.id,
			assistantCreatedAt: lastAssistant.createdAt,
			seedParts: lastAssistant.parts,
			llmModel,
			agentTools,
			system: withLoadedSkillInstructions(
				system,
				modelMessages,
				getAgentChatSession().loadedSkillNames,
				skillAvailability,
			),
			abortSignal,
			onMessagesChange,
			onToolExecutionEnd,
			requestAnimationFrame: raf,
			cancelAnimationFrame: caf,
			textOnlyAfterDenial,
		});
	} catch (caught) {
		return mapCaughtError(caught);
	}
}
